import classnames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  UnitPageContext,
  UnitPageContextInterface,
} from '../../../pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { getHrefForBuildingLink } from '../../Search/utils/listingsFiltersUtils';
import { Agent } from 'apolloClient/types/Agents';
import PopUpContactUs from 'components/ContactUs/PopUpContactUs';
import { buildRprQuery } from './buildRprQuery';

interface Props {
  experts: Agent[];
}

declare global {
  interface Window {
    rprAvmWidgetOptions?: any;
    rprAvmWidgetCompleteCallback?: (
      status: number,
      statusText: string,
      options: any,
      container: HTMLElement | null
    ) => void;
  }
}

const CondoEstimatesDisclosurePanel: React.FC<Props> = ({ experts }) => {
  const [isError, setIsError] = useState(false);

  const contentRef: { current: HTMLElement | null } = useRef(null);
  const { listing, building, neighborhood } = useContext(
    UnitPageContext
  ) as UnitPageContextInterface;

  useEffect(() => {
    window.rprAvmWidgetCompleteCallback = (status) => {
      if (status !== 0) {
        setIsError(true);
      }
    };

    createScriptWithVariable();

    if (!window.rprAvmWidgetOptions) {
      setIsError(true);
      return;
    }

    loadEstimateScript();

    return () => {
      delete window.rprAvmWidgetCompleteCallback;
      delete window.rprAvmWidgetOptions;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function createScriptWithVariable() {
    const { unitNumber, address } = listing;
    const { primaryAddress } = building;

    // address can be null at runtime for some listings; without it we can't
    // build the RPR query, so bail out and let the error guard below render
    // the graceful fallback message.
    if (!address) return;

    const { streetDirection, streetName, streetNumber, streetType } = address;

    const primaryAddressParts = primaryAddress.split(',');
    let city = '';
    let zipCode = '';
    if (primaryAddressParts.length === 2) {
      [zipCode] = primaryAddressParts[1].split(' ').slice(-1);
      city = primaryAddressParts[1].replace(zipCode, '').trim();
    } else [city, zipCode] = primaryAddressParts.slice(-2);

    // set directly on window object to ensure it's available before widget script runs
    window.rprAvmWidgetOptions = {
      Token: '3DBFFF2D-70B3-400A-895A-F1BBDB9DCFA2',
      Query: buildRprQuery({
        streetNumber,
        streetDirection,
        streetName,
        streetType,
        unitNumber,
        city,
        zipCode,
      }),
      CoBrandCode: 'btshbroswellrealty',
      ShowRprLinks: false,
      ContainerSelector: '#rpr-widget-container',
    };
  }

  function loadEstimateScript() {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://www.narrpr.com/widgets/avm-widget/widget.ashx/script';

    script.onerror = () => {
      setIsError(true);
    };

    document.head.appendChild(script);
  }

  return (
    <div
      className={classnames('flex flex-col text-14-21-0.3 md:text-16-26-0.3', {
        'lg:flex-row': !isError,
        'text-center': isError,
      })}
    >
      <div
        className={classnames('mb-[47px] ov', {
          'lg:w-2/5': !isError,
        })}
      >
        {
          'Buyers and Sellers can use this third-party automated valuation as a starting point for the potential condo sale value at '
        }
        <a
          rel="nofollow"
          className="text-gold-darker"
          href={getHrefForBuildingLink({ neighborhood, building })}
        >
          {building.name}
        </a>
        {
          '. The actual value of the condo is based on many factors that may not always be available in an automated valuation, so we suggest you '
        }
        <PopUpContactUs
          contactUsNotButton="contact us"
          contactUsNotButtonClassName="inline cursor-pointer text-gold-darker"
          experts={experts}
        />

        {' for an opinion from one of our real estate experts.'}
      </div>
      <div
        ref={(ref) => { contentRef.current = ref; }}
        id="rpr-widget-container"
        className="relative w-full lg:w-3/5 lg:mx-10"
      />
      {isError ? (
        <p>
          Sorry, the automated estimation tool did not offer a condo sale value,
          but feel free to contact us for our opinion of value.
        </p>
      ) : null}
    </div>
  );
};

export default CondoEstimatesDisclosurePanel;
